import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";

export interface ProductFiscal {
  id: string;
  name: string;
  category_id: string | null;
  ncm: string | null;
  cest: string | null;
  cfop: string | null;
  origem: string | null;
  unidade_comercial: string | null;
  tax_rule_id: string | null;
  category?: {
    name: string;
  };
  tax_rule?: {
    name: string;
  };
}

export interface FiscalBatchData {
  ncm?: string | null;
  cest?: string | null;
  cfop?: string | null;
  origem?: string | null;
  unidade_comercial?: string | null;
  tax_rule_id?: string | null;
}

export function useProductsFiscal() {
  const { activeRestaurant } = useRestaurantContext();
  const { logOperation } = useOperationLogs();
  const [products, setProducts] = useState<ProductFiscal[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    if (!activeRestaurant?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("products")
        .select(`
          id,
          name,
          category_id,
          ncm,
          cest,
          cfop,
          origem,
          unidade_comercial,
          tax_rule_id,
          categories:category_id (name),
          tax_rules:tax_rule_id (name)
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;

      const formattedProducts = (data || []).map((p: any) => ({
        ...p,
        category: p.categories,
        tax_rule: p.tax_rules,
      }));

      setProducts(formattedProducts);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
    } finally {
      setLoading(false);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const updateProductFiscal = async (productId: string, data: FiscalBatchData) => {
    try {
      const { error } = await supabase
        .from("products")
        .update(data)
        .eq("id", productId);

      if (error) throw error;

      await logOperation("update", "product", productId, {
        fiscalUpdate: true,
        changes: data,
      });

      toast.success("Dados fiscais atualizados!");
      await fetchProducts();
      return true;
    } catch (error) {
      console.error("Erro ao atualizar produto:", error);
      toast.error("Erro ao atualizar dados fiscais");
      return false;
    }
  };

  const updateProductsFiscalBatch = async (productIds: string[], data: FiscalBatchData) => {
    if (!productIds.length) return false;

    // Remove campos undefined/null que não devem ser atualizados
    const updateData: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        updateData[key] = value;
      }
    });

    if (Object.keys(updateData).length === 0) {
      toast.error("Nenhum campo selecionado para atualizar");
      return false;
    }

    try {
      const { error } = await supabase
        .from("products")
        .update(updateData)
        .in("id", productIds);

      if (error) throw error;

      await logOperation("update", "product", undefined, {
        batchFiscalUpdate: true,
        count: productIds.length,
        changes: updateData,
      });

      toast.success(`${productIds.length} produto(s) atualizado(s)!`);
      await fetchProducts();
      return true;
    } catch (error) {
      console.error("Erro ao atualizar produtos em lote:", error);
      toast.error("Erro ao atualizar produtos");
      return false;
    }
  };

  const clearProductsFiscalBatch = async (productIds: string[], fields: (keyof FiscalBatchData)[]) => {
    if (!productIds.length || !fields.length) return false;

    const clearData: Record<string, null> = {};
    fields.forEach((field) => {
      clearData[field] = null;
    });

    try {
      const { error } = await supabase
        .from("products")
        .update(clearData)
        .in("id", productIds);

      if (error) throw error;

      toast.success(`Dados fiscais limpos de ${productIds.length} produto(s)!`);
      await fetchProducts();
      return true;
    } catch (error) {
      console.error("Erro ao limpar dados fiscais:", error);
      toast.error("Erro ao limpar dados fiscais");
      return false;
    }
  };

  return {
    products,
    loading,
    fetchProducts,
    updateProductFiscal,
    updateProductsFiscalBatch,
    clearProductsFiscalBatch,
  };
}
